import { useCallback, useEffect, useState } from 'react';

import { envConfig } from '#constants/envConfig';
import { FLEXIBLE_POLL_MS } from '../constants';

/**
 * Что показывать на кнопке баннера.
 *
 * `downloading` — Play качает в фоне, приложение работает как обычно; `ready` — скачанное
 * лежит и ждёт установки, она случится только по нашему вызову.
 */
export type FlexibleStage = 'idle' | 'downloading' | 'ready';

/**
 * Плагин грузим динамически и только внутри вызовов. Статический импорт вернул бы чанк
 * Capacitor в веб-бандл: `IS_NATIVE` сворачивается в `false` и вырезает ветку, но только если
 * импорт стоит внутри неё (та же причина расписана в `utils/playUpdate.ts`).
 *
 * Промис держим один на модуль, а не создаём `import()` на каждый вызов: так эффект и
 * обработчик кнопки гарантированно работают с одним и тем же экземпляром модуля. Это не
 * микрооптимизация — на разных экземплярах проверки расходились, и обработчик уходил в ветку
 * «Play ничего не может», пока эффект видел обратное.
 */
let playModule: Promise<typeof import('../utils/playUpdate')> | null = null;

const play = () => (playModule ??= import('../utils/playUpdate'));

/**
 * Flexible-поток Google Play для баннера «доступна новая версия».
 *
 * Зачем он вместо простого «открыть магазин». Уход в Play — это выход из приложения, ручной
 * поиск кнопки «Обновить» и возврат назад; на этом пути теряется большинство. Flexible вместо
 * этого показывает нативный лист Play прямо поверх приложения, качает в фоне и не мешает
 * пользоваться продуктом, а установка занимает секунды.
 *
 * Immediate-поток здесь не используется намеренно: он забирает экран целиком и уместен только
 * там, где человеку всё равно некуда идти, то есть на экране жёсткой блокировки.
 *
 * Спрашивать Play имеет смысл только когда баннер на экране, поэтому хук принимает `active`:
 * без него каждый холодный старт дёргал бы плагин впустую, в том числе на свежей сборке, где
 * обновляться не на что.
 *
 * **Магазин остаётся фолбэком.** In-app updates работают лишь для сборки, поставленной из Play,
 * подписанной релизным ключом и принадлежащей текущему аккаунту. Эмулятор, sideload, debug,
 * отсутствующие Play Services — всё это штатные состояния, в которых Play отвечает «нельзя»,
 * и тогда мы ведём в магазин, как вели раньше.
 */
export const useFlexibleUpdate = (active: boolean) => {
  const [stage, setStage] = useState<FlexibleStage>('idle');

  /**
   * Спрашиваем Play один раз при появлении баннера: скачанное flexible-обновление переживает
   * перезапуск приложения. Без этой проверки человек, закрывший приложение на середине
   * загрузки, снова увидел бы «Обновить» и запустил бы всё заново вместо установки готового.
   */
  useEffect(() => {
    if (!envConfig.IS_NATIVE || !active) {
      return;
    }

    let alive = true;
    void (async () => {
      const { getUpdateInfo } = await play();
      const info = await getUpdateInfo();
      if (alive && info.downloaded) {
        setStage('ready');
      }
    })();

    return () => {
      alive = false;
    };
  }, [active]);

  /**
   * Прогресса загрузки Play наружу не отдаёт — только признак «скачано», поэтому опрашиваем.
   * Таймер живёт ровно столько, сколько идёт загрузка: в остальное время дёргать плагин незачем.
   */
  useEffect(() => {
    if (!envConfig.IS_NATIVE || !active || stage !== 'downloading') {
      return;
    }

    const id = window.setInterval(() => {
      void (async () => {
        const { getUpdateInfo } = await play();
        if ((await getUpdateInfo()).downloaded) {
          setStage('ready');
        }
      })();
    }, FLEXIBLE_POLL_MS);

    return () => window.clearInterval(id);
  }, [active, stage]);

  /**
   * Нажатие на «Обновить».
   *
   * `fallback` зовём только тогда, когда Play принципиально не может показать свой диалог.
   * Отказ человека в диалоге — это осознанное «не сейчас», и утаскивать его после этого в
   * магазин было бы ровно тем поведением, из-за которого баннеры обновления и раздражают.
   */
  const start = useCallback((fallback: () => void) => {
    void (async () => {
      if (!envConfig.IS_NATIVE) {
        fallback();
        return;
      }

      const { getUpdateInfo, startFlexibleUpdate } = await play();
      const info = await getUpdateInfo();

      if (info.downloaded) {
        setStage('ready');
        return;
      }

      if (info.available && info.flexibleAllowed) {
        if (await startFlexibleUpdate()) {
          setStage('downloading');
        }
        return;
      }

      fallback();
    })();
  }, []);

  /**
   * Ставит скачанное и перезапускает приложение. Возврат `false` означает, что ставить нечего
   * (Play успел применить обновление сам или человек его отозвал), — тогда возвращаем кнопку
   * в исходное состояние, чтобы на экране не осталось «Перезапустить», которое ничего не делает.
   */
  const complete = useCallback(() => {
    void (async () => {
      const { completeFlexibleUpdate } = await play();
      if (!(await completeFlexibleUpdate())) {
        setStage('idle');
      }
    })();
  }, []);

  return { stage, start, complete };
};
